import { Badge, Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useQuery } from "react-query";
import { SimpleTable } from "@/v2/components/table/SimpleTable";
import { ColumnDef } from "@tanstack/react-table";
import { AuiRegressionTestJudgeDistinctPair } from "@/v2/entities/regression/Regression.model";

export function AuiRegressionTestsList() {
  const navigate = useNavigate();
  const { getDistinctAuiRegressionTestIdPairs } = useRegressionApi();

  const { data: pairs = [], isLoading } = useQuery({
    queryKey: ["aui-regression-tests", "distinct-pairs"],
    queryFn: getDistinctAuiRegressionTestIdPairs,
  });

  const handleRowClick = useCallback(
    (row: AuiRegressionTestJudgeDistinctPair) => {
      navigate(
        `/automation-center/aui-regression-tests/${row.manualRegressionTestId}/${row.auiRegressionTestId}`
      );
    },
    [navigate]
  );

  const columns = useMemo<ColumnDef<AuiRegressionTestJudgeDistinctPair>[]>(
    () => [
      {
        id: "manualRegressionTestId",
        accessorKey: "manualRegressionTestId",
        header: "Manual Test ID",
        cell: ({ getValue }) => (
          <Text size="2" className="font-mono">
            {String(getValue()).slice(-12)}
          </Text>
        ),
      },
      {
        id: "auiRegressionTestId",
        accessorKey: "auiRegressionTestId",
        header: "AUI Test ID",
        cell: ({ getValue }) => (
          <Text size="2" className="font-mono">
            {String(getValue()).slice(-12)}
          </Text>
        ),
      },
      {
        id: "regressionSetName",
        accessorKey: "regressionSetName",
        header: "Regression Set",
        cell: ({ getValue }) => (
          <Badge color="gray" variant="soft" size="1">
            {String(getValue())}
          </Badge>
        ),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ getValue }) => (
          <Text size="2">
            {new Date(String(getValue())).toLocaleString()}
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="6" px="6" py="6">
      <Flex direction="row" gap="2" justify="between">
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Button
              variant="ghost"
              size="1"
              onClick={() => navigate("/automation-center/regression-tests")}
            >
              <ArrowLeftIcon />
            </Button>
            <Text size="5" weight="medium">
              AUI Regression Test Results Judge
            </Text>
          </Flex>
          <Text size="2" color="gray">
            Distinct manual / AUI regression test pairs. Click a row to see
            judge results.
          </Text>
        </Flex>
        <Button
          variant="soft"
          onClick={() => navigate("/automation-center/aui-regression-tests/v2")}
        >
          Open v2 (local results)
        </Button>
      </Flex>

      <SimpleTable
        data={pairs}
        columns={columns}
        loading={isLoading}
        onRowClicked={handleRowClick}
        getRowId={(row) => `${row.manualRegressionTestId}-${row.auiRegressionTestId}`}
      />
    </Flex>
  );
}
